import React, { useState } from "react";
import Dropdown, { Option } from "../customer/Dropdown";
import { calendarOptions } from "../../utils/Customer/CustomerData";
import { ChevronDown } from "lucide-react";

type DonutChartItem = {
    label: string;
    value: number;
    color: string;
};

type DonutChartCardProps = {
    title?: string;
    totalLabel?: string;
    totalValue: string;
    data: DonutChartItem[];
};

const DonutChartCard: React.FC<DonutChartCardProps> = ({
    title = "Category Analytics",
    totalLabel = "Total",
    totalValue,
    data,
}) => {
    const radius = 110;
    const strokeWidth = 30;
    const normalizedRadius = radius - strokeWidth / 2;
    const circumference = normalizedRadius * 2 * Math.PI;

    const [options, setOptions] = useState<Option | null>(
        calendarOptions[1]
    );

    // Calculate total count
    const dataTotal = data.reduce(
        (sum, item) => sum + (Number(item.value) || 0),
        0
    );

    let cumulativePercent = 0;

    return (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 h-full w-full">

            {/* HEADER */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-logoHeading flex-1">
                    {title}
                </h2>
            </div>

            {/* CONTENT */}
            <div className="flex flex-col">

                {/* DONUT CHART */}
                <div className="relative w-full h-[220px] flex items-center justify-center pb-4">

                    <svg
                        height={radius * 2 + 40}
                        width={radius * 2 + 40}
                        viewBox={`0 0 ${radius * 2 + 40} ${radius * 2 + 40}`}
                        className="rotate-[-90deg]"
                        shapeRendering="geometricPrecision"
                    >
                        {data.map((item, index) => {
                            // Convert count into percentage
                            const percent =
                                dataTotal > 0
                                    ? item.value / dataTotal
                                    : 0;

                            // Keep a small gap between segments
                            const dashLength = Math.max(
                                percent * circumference - 2,
                                0
                            );

                            const strokeDasharray = `${dashLength} ${circumference}`;

                            const strokeDashoffset =
                                -cumulativePercent * circumference;

                            cumulativePercent += percent;

                            return (
                                <circle
                                    key={index}
                                    stroke={item.color}
                                    fill="transparent"
                                    strokeWidth={strokeWidth}
                                    strokeDasharray={strokeDasharray}
                                    strokeDashoffset={strokeDashoffset}
                                    strokeLinecap="butt"
                                    r={normalizedRadius}
                                    cx={radius + 20}
                                    cy={radius + 20}
                                />
                            );
                        })}
                    </svg>

                    {/* CENTER TEXT */}
                    <div className="absolute flex flex-col items-center justify-center">
                        <span className="text-sm text-gray-500">
                            {totalLabel}
                        </span>

                        <h3 className="text-lg font-bold text-logoHeading mt-1">
                            {totalValue}
                        </h3>
                    </div>
                </div>

                {/* LEGENDS */}
                <div className="flex-1 w-full space-y-4">
                    {data.map((item, index) => {
                        const percentage =
                            dataTotal > 0
                                ? (item.value / dataTotal) * 100
                                : 0;

                        return (
                            <div
                                key={index}
                                className="flex items-center justify-between gap-3"
                            >
                                <div className="flex items-center gap-3">

                                    <div
                                        className="w-3 h-3 rounded-full"
                                        style={{
                                            backgroundColor: item.color,
                                        }}
                                    />

                                    <p className="text-sm font-medium text-gray-700">
                                        {item.label}
                                    </p>
                                </div>

                                <span className="text-sm text-gray-500 font-medium">
                                    {percentage.toFixed(0)}%
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default DonutChartCard;